/**
 * Centralized URL constants for the e-hentai / exhentai sources.
 *
 * Mirrors [EhViewer's `EhUrl.kt`](https://github.com/FooIbar/EhViewer/blob/main/app/src/main/kotlin/com/hippo/ehviewer/client/EhUrl.kt),
 * trimmed to what our SPA actually uses. The same module serves both sites;
 * pick a {@link Host} and use the helpers below.
 *
 * Always send `nw=1` on `e-hentai.org` requests so the New User warning
 * interstitial doesn't intercept the response (a quirk EhViewer documents).
 */

export const HOST_E = 'https://e-hentai.org';
export const HOST_EX = 'https://exhentai.org';
export const HOST_EHGT = 'https://ehgt.org';

export const API_E = 'https://api.e-hentai.org/api.php';
export const API_EX = 'https://s.exhentai.org/api.php';

export const URL_FORUMS = 'https://forums.e-hentai.org';
export const URL_SIGN_IN = 'https://forums.e-hentai.org/index.php?act=Login&CODE=01';

export const URL_UCONFIG_E = `${HOST_E}/uconfig.php`;
export const URL_UCONFIG_EX = `${HOST_EX}/uconfig.php`;

export const URL_FAVORITES_E = `${HOST_E}/favorites.php`;
export const URL_FAVORITES_EX = `${HOST_EX}/favorites.php`;

export const URL_HOME_E = `${HOST_E}/home.php`;
export const URL_HOME_EX = `${HOST_EX}/home.php`;

export const URL_POPULAR_E = `${HOST_E}/popular`;
export const URL_POPULAR_EX = `${HOST_EX}/popular`;

export const URL_WATCHED_E = `${HOST_E}/watched`;
export const URL_WATCHED_EX = `${HOST_EX}/watched`;

export const URL_TOPLIST = 'https://e-hentai.org/toplist.php';

/** Endpoint that handles add/remove favorite POSTs. Same path on both sites. */
export const ENDPOINT_GALLERY_POPUPS = '/gallerypopups.php';

/** The two sites we know about. */
export type Host = typeof HOST_E | typeof HOST_EX;

/** Discriminator the source modules pass through everything. */
export type SiteId = 'ehentai' | 'exhentai';

export interface SiteContext {
  id: SiteId;
  /** Base origin, no trailing slash. */
  host: Host;
  /** `api.php` endpoint. */
  api: string;
  /** `Referer` / `Origin` header to send with POSTs. */
  origin: string;
  /** `/favorites.php` for this site. */
  favoritesUrl: string;
  /** `/uconfig.php` for this site. */
  uconfigUrl: string;
  /** `/popular` for this site. */
  popularUrl: string;
  /** `/watched` for this site. */
  watchedUrl: string;
}

export const SITE_E: SiteContext = {
  id: 'ehentai',
  host: HOST_E,
  api: API_E,
  origin: HOST_E,
  favoritesUrl: URL_FAVORITES_E,
  uconfigUrl: URL_UCONFIG_E,
  popularUrl: URL_POPULAR_E,
  watchedUrl: URL_WATCHED_E,
};

export const SITE_EX: SiteContext = {
  id: 'exhentai',
  host: HOST_EX,
  api: API_EX,
  origin: HOST_EX,
  favoritesUrl: URL_FAVORITES_EX,
  uconfigUrl: URL_UCONFIG_EX,
  popularUrl: URL_POPULAR_EX,
  watchedUrl: URL_WATCHED_EX,
};

/**
 * Auto-append the `nw=1` query parameter on `e-hentai.org` URLs to bypass
 * the "New User" warning interstitial. exhentai never shows it.
 *
 * Already-present `nw` is preserved verbatim (the value doesn't matter).
 */
export function withNw(url: string, site: SiteContext): string {
  if (site.id !== 'ehentai') return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.host !== 'e-hentai.org' && parsed.host !== 'www.e-hentai.org') return url;
  if (parsed.searchParams.has('nw')) return parsed.toString();
  parsed.searchParams.set('nw', '1');
  return parsed.toString();
}

/** Build the gallery detail page URL: `/g/{gid}/{token}/`. */
export function galleryUrl(site: SiteContext, gid: number | string, token: string): string {
  return `${site.host}/g/${gid}/${token}/`;
}

/**
 * Build the page-view URL: `/s/{ptoken}/{gid}-{page1Based}`. This is the
 * single page where `showkey` is set; subsequent pages should go through
 * `showpage` on the `api.php` endpoint.
 */
export function pageUrl(
  site: SiteContext,
  ptoken: string,
  gid: number | string,
  page1Based: number,
): string {
  return `${site.host}/s/${ptoken}/${gid}-${page1Based}`;
}

/** Build the multi-page-viewer URL: `/mpv/{gid}/{token}`. */
export function mpvUrl(site: SiteContext, gid: number | string, token: string): string {
  return `${site.host}/mpv/${gid}/${token}`;
}

/**
 * ExHentai sometimes emits thumbnail / cover URLs on `exhentai.org` hosts.
 * Those URLs require the user's ExHentai cookies, which browser `<img>`
 * requests from our app cannot attach. The same static image paths are served
 * from `ehgt.org`, so normalize them before they reach React image tags.
 */
export function publicEhImageUrl(url: string, site: SiteContext): string {
  if (!url || site.id !== 'exhentai') return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.host !== 'exhentai.org' && !parsed.host.endsWith('.exhentai.org')) return url;
  parsed.protocol = 'https:';
  parsed.host = 'ehgt.org';
  return parsed.toString();
}
